import NavBar from "../../component/navBar/NavBar";
import Title from "../../component/title/Title";
import Table from "../../component/table/Table";
import { DispatchOccupancyModal } from "../../component/table/DispatchOccupancyCell";
import useVehicleWebSocket from "../../config/utilities/useVehicleWebSocket";
import {
  areDispatchOccupancyModalDataEqual,
  buildDispatchOccupancyModalData,
  filterDispatchBoardTrips,
  hasDispatchOnboardStudents,
} from "../../config/utilities/dispatchOccupancyHelpers";
import { useCallback, useMemo, useRef, useState } from "react";
import "./DispatchBoard.css";

const headers = [
  { key: "vehicleNumber", name: "Registration Number", sortable: false },
  { key: "tripName", name: "Trip Name", sortable: false },
  { key: "prevStopData", name: "Previous Stop", sortable: false },
  { key: "nextStopData", name: "Next Stop", sortable: false },
  { key: "nextStopArrivalData", name: "Next Stop Arrival", sortable: false },
  { key: "finalStopData", name: "Final Stop", sortable: false },
  { key: "finalStopArrivalData", name: "Final Stop Arrival", sortable: false },
  { key: "occupancyData", name: "Occupancy", sortable: false },
  { key: "tripStatus", name: "Status", sortable: false },
];

export default function DispatchBoard() {
  const { dispatchData, loading } = useVehicleWebSocket();
  const [searchTerm, setSearchTerm] = useState("");
  const [occupancyModalRowId, setOccupancyModalRowId] = useState(null);
  const occupancyModalDataRef = useRef(null);

  const handleDispatchRowClick = useCallback((row) => {
    if (hasDispatchOnboardStudents(row) && row?.documentId) {
      setOccupancyModalRowId(row.documentId);
    }
  }, []);

  const handleCloseOccupancyModal = useCallback(() => {
    setOccupancyModalRowId(null);
    occupancyModalDataRef.current = null;
  }, []);

  const isDispatchRowClickable = useCallback(
    (row) => hasDispatchOnboardStudents(row),
    [],
  );

  const dispatchTableData = useMemo(
    () => filterDispatchBoardTrips(dispatchData),
    [dispatchData],
  );

  const occupancyModalData = useMemo(() => {
    if (!occupancyModalRowId) {
      occupancyModalDataRef.current = null;
      return null;
    }

    const row = dispatchTableData.find((entry) => entry.documentId === occupancyModalRowId);
    if (!row || !hasDispatchOnboardStudents(row)) {
      occupancyModalDataRef.current = null;
      return null;
    }

    const nextModalData = buildDispatchOccupancyModalData(row);
    if (
      occupancyModalDataRef.current &&
      areDispatchOccupancyModalDataEqual(occupancyModalDataRef.current, nextModalData)
    ) {
      return occupancyModalDataRef.current;
    }

    occupancyModalDataRef.current = nextModalData;
    return nextModalData;
  }, [occupancyModalRowId, dispatchTableData]);

  return (
    <div className="main-page dispatch-board-page">
      <NavBar type="dispatchBoard" />
      <div className="content-area dispatch-board-content-area">
        <div className="title-container dispatch-board-title-container">
          <Title
            title="Dispatch Board"
            subtitle="Real-time dispatch board to track vehicles and their trips"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        </div>
        <div className="main-content-container dispatch-board-main-content-container">
          <Table
            data={dispatchTableData}
            searchText={"Search by Registration Number..."}
            headers={headers}
            module="dispatch"
            loading={loading}
            showTableAction={false}
            height="75vh"
            useBackendPagination={false}
            showSearchBar={true}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            enableMobileCards={true}
            onRowClick={handleDispatchRowClick}
            isRowClickable={isDispatchRowClickable}
          />
        </div>
      </div>

      <DispatchOccupancyModal
        isOpen={Boolean(occupancyModalRowId && occupancyModalData)}
        onClose={handleCloseOccupancyModal}
        childrenOnboard={occupancyModalData?.childrenOnboard ?? []}
        occupancyLabel={occupancyModalData?.occupancyLabel ?? "0/0"}
        tripName={occupancyModalData?.tripName}
        vehicleLabel={occupancyModalData?.vehicleLabel}
      />
    </div>
  );
}
